import React, { useState, useMemo } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
	GenericTable,
	GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { DataStateView, EmptyStateView } from "../StateViews";
import {
	connectedServiceTypeToGroup,
	isBgpStatusFilterUnfiltered,
	isConnectorTypeFilterUnfiltered,
	isVrfFilterUnfiltered,
	connectorIsEstablishedToBgpStatusFilterId,
	matchesBgpStatusFilterSelection,
	matchesConnectorTypeGroupSelection,
	matchesVrfFilterSelection,
} from "../BackboneDetailsData";
import {
	GLOBAL_BW,
	typeColors,
	formatBandwidthFromMbpsComparePair,
	formatBandwidthFromMbpsInline,
	formatBandwidthMbpsForDisplay,
	roundBw,
} from "../../backbone.data";
import { UsageBar } from "../BackboneCharts";
import {
	bbConnectorServiceSubscriptionId,
	formatBbConnectorConnectedServiceTypeLabel,
	mapConnectedServiceTypeToConnectorType,
	type BbConnectorListItem,
} from "../../requests/request";
import { useAppSelector } from "@redux/hooks";
import type { RootState } from "@redux/store/store";
import { connectorTypeLabel } from "./connectorFieldSpec";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import {
	buildPredicate,
	type FilterField,
	labelId,
	parseToken,
	type ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import { makeConnectorFields } from "./connectorFieldSpec";
import {
	SkeletonBlock,
	TableSkeleton,
} from "@ui-components/Skeleton/GenericSkeleton";
import ConnectorsModal from "./ConnectorsModal";

interface ConnectorsTabProps {
	/** Connector list from API (BB-Connector). When empty, table shows empty state. */
	connectors?: BbConnectorListItem[];
	vrfFilterSelection?: string[];
	connectorTypeFilterSelection?: string[];
	bgpStatusFilterSelection?: string[];
	loading?: boolean;
	error?: string | null;
	onRetry?: () => void;
}

/** Table column headers; used for searchbar suggested keys (only filterable columns). */
const CONNECTOR_TABLE_HEADERS = [
	"Type",
	"Connector",
	"Location",
	"VRF",
	"VLAN",
	"BGP",
];

const BANDWIDTH_LIST_SCROLL_THRESHOLD = 10;
/** Max height of the scrollable connector list inside the bandwidth card (from this count upward). */
const BANDWIDTH_LIST_MAX_HEIGHT_CLASS = "max-h-72";

const ConnectorsTab: React.FC<ConnectorsTabProps> = ({
	connectors = [],
	vrfFilterSelection = [],
	connectorTypeFilterSelection = [],
	bgpStatusFilterSelection = [],
	loading = false,
	error = null,
	onRetry,
}) => {
	const selectionTenantId = useAppSelector((state: RootState) => state.selection.tenantId);
	const tenantId = selectionTenantId ?? null;

	const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
	const [selectedConnector, setSelectedConnector] = useState<BbConnectorListItem | null>(null);

	const data = useMemo(
		() =>
			connectors.filter((c) => {
				if (!matchesVrfFilterSelection(c.vrf?.displayName, vrfFilterSelection)) return false;
				const group = connectedServiceTypeToGroup(c.connectedServiceType);
				if (!matchesConnectorTypeGroupSelection(group, connectorTypeFilterSelection))
					return false;
				return matchesBgpStatusFilterSelection(
					connectorIsEstablishedToBgpStatusFilterId(c.bgpStatus?.isEstablished),
					bgpStatusFilterSelection
				);
			}),
		[connectors, vrfFilterSelection, connectorTypeFilterSelection, bgpStatusFilterSelection]
	);

	const connectorFields = useMemo(() => makeConnectorFields(), []);

	const fieldById = useMemo(() => {
		const m = new Map<string, FilterField<BbConnectorListItem>>();
		for (const f of connectorFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			m.set(id, f);
		}
		return m;
	}, [connectorFields]);

	const keyMap = useMemo(() => {
		const m = new Map<string, string>();
		for (const f of connectorFields) {
			const id = (f.key ?? labelId(f.label)).toLowerCase();
			const label = f.label.toLowerCase();
			m.set(id, id);
			m.set(label, id);
		}
		return m;
	}, [connectorFields]);

	const normalizedFilters = useMemo(() => {
		return activeFilters.map((f) => {
			const token = f.op
				? { ...f }
				: parseToken(f.key ? `${f.key}=${f.value}` : f.value);
			if (token.key) {
				const k = token.key.trim().toLowerCase();
				token.key = keyMap.get(k) ?? labelId(token.key);
			}
			const field = token.key ? fieldById.get(token.key) : undefined;
			if (field?.type === "string") {
				const quoted = token.value.match(/^"(.*)"$/);
				if (quoted) {
					token.value = quoted[1];
					token.op = "=";
				} else {
					token.op = ":";
				}
			}
			return token;
		});
	}, [activeFilters, keyMap, fieldById]);

	const predicate = useMemo(
		() => buildPredicate<BbConnectorListItem>(normalizedFilters, connectorFields),
		[normalizedFilters, connectorFields]
	);

	const filteredData = useMemo(() => data.filter(predicate), [data, predicate]);

	const bandwidthRankedConnectors = useMemo(() => {
		return filteredData
			.map((ep) => ({
				ep,
				bw: (ep.bandwidth?.inbound ?? 0) + (ep.bandwidth?.outbound ?? 0),
			}))
			.sort((a, b) => b.bw - a.bw);
	}, [filteredData]);

	/** VRF scope only (matches header Current BW / Available Capacity; table search does not change totals). */
	const totalInbound = useMemo(
		() => data.reduce((s, e) => s + (e.bandwidth?.inbound ?? 0), 0),
		[data]
	);

	const totalOutbound = useMemo(
		() => data.reduce((s, e) => s + (e.bandwidth?.outbound ?? 0), 0),
		[data]
	);

	const isBandwidthEmpty = data.length === 0;
	const bandwidthListScrollable =
		bandwidthRankedConnectors.length >= BANDWIDTH_LIST_SCROLL_THRESHOLD;

	const columns = useMemo<GenericTableColumn<BbConnectorListItem>[]>(
		() => [
			{
				header: "Type",
				renderCell: (row) => {
					const label = formatBbConnectorConnectedServiceTypeLabel(row);
					const uiType = mapConnectedServiceTypeToConnectorType(row.connectedServiceType);
					const tc = typeColors[uiType] ?? typeColors.Port;
					return (
						<GenericTag
							label={label}
							variant="custom"
							backgroundColor={tc.bg}
							textColor={tc.text}
							size="small"
						/>
					);
				},
			},
			{
				header: "Connector",
				renderCell: (row) => {
					const d = row.connectedServiceDetail;
					if (d && typeof d === "object") {
						const subId = d.subscriptionId != null ? String(d.subscriptionId).trim() : "";
						const detailName = d.name != null ? String(d.name).trim() : "";
						const line1 = subId !== "" ? subId : "—";
						const line2 = detailName !== "" ? detailName : "—";
						return (
							<div className="flex flex-col">
								<span
									className={`font-medium text-semantic-text-primary text-sm ${
										subId !== "" ? "font-mono" : ""
									}`}
								>
									{line1}
								</span>
								<span className="text-xs text-semantic-text-secondary">
									{line2}
								</span>
							</div>
						);
					}
					const serviceSubId = bbConnectorServiceSubscriptionId(row);
					const primary = serviceSubId || row.name.trim() || "—";
					const showNameSub =
						serviceSubId !== "" &&
						row.name.trim() !== "" &&
						row.name.trim() !== serviceSubId;
					return (
						<div className="flex flex-col">
							<span
								className={`font-medium text-semantic-text-primary text-sm ${
									serviceSubId !== "" ? "font-mono" : ""
								}`}
							>
								{primary}
							</span>
							{showNameSub && (
								<span className="text-xs text-semantic-text-secondary">
									{row.name}
								</span>
							)}
						</div>
					);
				},
			},
			{
				header: "Location",
				renderCell: (row) => (
					<span className="text-sm text-semantic-text-secondary">
						{row.location?.displayName ?? "—"}
					</span>
				),
			},
			{
				header: "VRF",
				renderCell: (row) => (
					<GenericTag
						label={row.vrf?.displayName || "—"}
						variant="neutral"
						size="small"
					/>
				),
			},
			{
				header: "VLAN",
				renderCell: (row) => (
					<span className="text-sm text-semantic-text-primary">{row.vlan ?? "—"}</span>
				),
			},
			{
				header: "BGP",
				renderCell: (row) => {
					const isUp = row.bgpStatus?.isEstablished ?? false;
					return (
						<GenericTag
							label={isUp ? "Up" : "Down"}
							variant={isUp ? "success" : "error"}
							size="small"
						/>
					);
				},
			},
			{
				header: (
					<div className="flex flex-col">
						<span>Bandwidth</span>
						<span className="text-xs font-normal text-semantic-text-secondary">
							Inbound / Outbound
						</span>
					</div>
				),
				renderCell: (row) => {
					const bw = row.bandwidth;
					if (bw == null) {
						return <span className="text-sm text-semantic-text-secondary">—</span>;
					}
					const inbound = bw.inbound ?? 0;
					const outbound = bw.outbound ?? 0;
					const inDisp = formatBandwidthMbpsForDisplay(inbound);
					const outDisp = formatBandwidthMbpsForDisplay(outbound);
					return (
						<div className="flex flex-col gap-0.5">
							<span className="text-sm text-semantic-text-primary tabular-nums">
								{inDisp.value} / {outDisp.value}
							</span>
							<span className="text-[10px] leading-tight text-semantic-text-secondary tabular-nums">
								{inDisp.unit} / {outDisp.unit}
							</span>
						</div>
					);
				},
			},
			{
				header: "Details",
				renderCell: (row) => (
					<div className="flex items-center gap-1">
						<button
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								setSelectedConnector(row);
							}}
							className="p-1 rounded hover:bg-semantic-bg-tertiary text-semantic-text-secondary hover:text-semantic-text-primary transition-colors"
							aria-label="View details"
						>
							<IconEye className="w-5 h-5" />
						</button>
					</div>
				),
			},
		],
		[]
	);

	return (
		<>
			{/* Backbone Bandwidth */}
			<GenericCard
				isCollapsible={false}
				className="mt-4"
				headerContent={
					<h2 className="text-md font-bold text-semantic-text-primary">
						Backbone Bandwidth
					</h2>
				}
				bodyContent={
					<DataStateView
						loading={loading}
						error={error}
						isEmpty={false}
						onRetry={onRetry}
						loadingContent={
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
								<div className="w-full min-w-0 rounded-md border border-semantic-border-primary bg-semantic-bg-secondary p-4">
									<SkeletonBlock h={14} w="60%" className="mb-3" />
									<div className="space-y-2">
										{Array.from({ length: 10 }).map((_, i) => (
											<div key={i} className="flex items-center gap-2">
												<SkeletonBlock h={10} w={80} className="flex-shrink-0" />
												<SkeletonBlock h={10} className="flex-1 min-w-0" />
												<SkeletonBlock h={10} w={36} className="flex-shrink-0" />
											</div>
										))}
									</div>
								</div>
								<div className="flex flex-col gap-3 w-full min-w-0">
									<div className="grid grid-cols-3 gap-3">
										{Array.from({ length: 3 }).map((_, i) => (
											<SkeletonBlock key={i} h={80} className="rounded-md" />
										))}
									</div>
									<SkeletonBlock h={56} className="rounded-md" />
								</div>
							</div>
						}
						errorTitle="Failed to load bandwidth"
						emptyTitle=""
						emptyDescription=""
					>
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
							{/* Left: connectors by bandwidth (respects global Filters dropdown). */}
							<GenericCard
								isCollapsible={false}
								className="w-full min-w-0"
								headerContent={
									<div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-3">
										<h3 className="text-sm font-medium text-semantic-text-primary shrink-0">
											Connectors List
										</h3>
									</div>
								}
								bodyContent={
									isBandwidthEmpty ? (
										<EmptyStateView
											title={
												isVrfFilterUnfiltered(vrfFilterSelection) &&
												isConnectorTypeFilterUnfiltered(connectorTypeFilterSelection) &&
												isBgpStatusFilterUnfiltered(bgpStatusFilterSelection)
													? "No bandwidth data"
													: "No bandwidth for current filters"
											}
											description={
												isVrfFilterUnfiltered(vrfFilterSelection) &&
												isConnectorTypeFilterUnfiltered(connectorTypeFilterSelection) &&
												isBgpStatusFilterUnfiltered(bgpStatusFilterSelection)
													? "Bandwidth metrics will appear when connector data is available."
													: "Try adjusting or clearing the active filters to see more connectors."
											}
										/>
									) : filteredData.length === 0 ? (
										<EmptyStateView
											title="No connectors to show"
											description="Adjust search filters to see connectors in this list."
										/>
									) : (
										<div
											className={`min-h-0 min-w-0 ${bandwidthListScrollable ? `${BANDWIDTH_LIST_MAX_HEIGHT_CLASS} overflow-y-auto pr-0.5` : ""}`}
										>
											<div className="space-y-1.5">
												{bandwidthRankedConnectors.map(({ ep, bw }) => {
													const displayLabel = connectorTypeLabel(ep);
													return (
														<div key={ep.id} className="flex items-center gap-2">
															<span
																className="w-[min(140px,32%)] flex-shrink-0 truncate text-right text-xs font-medium text-semantic-text-primary"
																title={displayLabel}
															>
																{displayLabel}
															</span>
															<div className="min-w-0 flex-1 overflow-hidden rounded bg-semantic-bg-tertiary h-2.5">
																<div
																	className="h-full rounded bg-[var(--color-semantic-bg-brand-bold)] opacity-70 transition-all duration-500"
																	style={{
																		width: `${GLOBAL_BW > 0 ? (bw / GLOBAL_BW) * 100 : 0}%`,
																	}}
																/>
															</div>
															<span className="min-w-[56px] flex-shrink-0 text-right text-xs text-semantic-text-primary tabular-nums">
																{formatBandwidthFromMbpsInline(roundBw(bw))}
															</span>
														</div>
													);
												})}
											</div>
										</div>
									)
								}
							/>

							{/* Right: metric cards — show "–" when no data */}
							<div className="flex min-w-0 w-full flex-col gap-3">
								<div className="grid grid-cols-2 gap-3">
									<GenericCard
										isCollapsible={false}
										variant="secondary"
										bodyContent={
											<>
												<span className="text-xs text-semantic-text-secondary">
													Inbound
												</span>
												<div className="text-xl font-bold text-semantic-text-success">
													{isBandwidthEmpty
														? "–"
														: formatBandwidthFromMbpsInline(roundBw(totalInbound))}
												</div>
												{!isBandwidthEmpty && (
													<div className="mt-1">
														<UsageBar
															used={totalInbound}
															cap={GLOBAL_BW}
															height={6}
															barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
															minWidthPercentWhenPositive={0.85}
														/>
													</div>
												)}
											</>
										}
									/>
									<GenericCard
										isCollapsible={false}
										variant="secondary"
										bodyContent={
											<>
												<span className="text-xs text-semantic-text-secondary">
													Outbound
												</span>
												<div className="text-xl font-bold text-semantic-text-success">
													{isBandwidthEmpty
														? "–"
														: formatBandwidthFromMbpsInline(roundBw(totalOutbound))}
												</div>
												{!isBandwidthEmpty && (
													<div className="mt-1">
														<UsageBar
															used={totalOutbound}
															cap={GLOBAL_BW}
															height={6}
															barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
															minWidthPercentWhenPositive={0.85}
														/>
													</div>
												)}
											</>
										}
									/>
								</div>
								<GenericCard
									isCollapsible={false}
									variant="secondary"
									bodyContent={
										<>
											<div className="mb-1 flex justify-between text-xs">
												<span className="text-semantic-text-secondary">
													Available Capacity
												</span>
												<span className="font-medium text-semantic-text-brand">
													{isBandwidthEmpty
														? "–"
														: formatBandwidthFromMbpsComparePair(
																Math.max(0, GLOBAL_BW - totalOutbound),
																GLOBAL_BW,
																3
															)}
												</span>
											</div>
											{!isBandwidthEmpty && (
												<UsageBar
													used={totalOutbound}
													cap={GLOBAL_BW}
													height={6}
													barClassName="bg-[var(--color-semantic-bg-brand-bold)]"
													minWidthPercentWhenPositive={0.85}
												/>
											)}
										</>
									}
								/>
							</div>
						</div>
					</DataStateView>
				}
			/>

			{/* Connectors Table */}
			<GenericCard
				isCollapsible={false}
				className="mt-4"
				headerContent={
					<h2 className="text-md font-bold text-semantic-text-primary">
						Backbone Connectors 
					</h2>
				}
				bodyContent={
					<DataStateView
						loading={loading}
						error={error}
						isEmpty={data.length === 0}
						onRetry={onRetry}
						loadingContent={
							<div className="flex flex-col min-h-0">
								<div className="mb-4">
									<SkeletonBlock h={40} w="100%" className="max-w-[500px] rounded" />
								</div>
								<TableSkeleton columns={10} rows={8} />
							</div>
						}
						errorTitle="Failed to load connectors"
						emptyTitle={
							isVrfFilterUnfiltered(vrfFilterSelection) &&
							isConnectorTypeFilterUnfiltered(connectorTypeFilterSelection) &&
							isBgpStatusFilterUnfiltered(bgpStatusFilterSelection)
								? "You have no Connectors yet"
								: "No connectors for current filters"
						}
						emptyDescription={
							isVrfFilterUnfiltered(vrfFilterSelection) &&
							isConnectorTypeFilterUnfiltered(connectorTypeFilterSelection) &&
							isBgpStatusFilterUnfiltered(bgpStatusFilterSelection)
								? "This backbone has no connectors. To add connectors, contact your account manager."
								: "There are no connectors matching the active filters. Try changing or clearing them."
						}
					>
						<div className="flex flex-col min-h-0">
							<div className="mb-4 flex-shrink-0">
								<div className="w-full max-w-full sm:max-w-[500px]">
									<GenericSearchbar
										placeholder="Find by attribute or value"
										suggestedKeys={CONNECTOR_TABLE_HEADERS}
										onFiltersChange={setActiveFilters}
									/>
								</div>
							</div>
							<div className="min-w-0">
								<GenericTable<BbConnectorListItem>
									columns={columns}
									data={filteredData}
									initialRowsPerPage={10}
									rowsPerPageOptions={[10, 20, 30]}
									width="100%"
									maxTableBodyHeight="100%"
									rowKey={(row) => String(row.id)}
									isFooterVisible={true}
									horizontalScroll="always"
								/>
							</div>
						</div>
					</DataStateView>
				}
			/>

			<ConnectorsModal
				connector={selectedConnector}
				onClose={() => setSelectedConnector(null)}
				tenantId={tenantId}
			/>
		</>
	);
};

export default ConnectorsTab;
